import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  Chip,
} from '@mui/material';

interface Option {
  id: string;
  name: string;
}

interface ProductOption extends Option {
  // e.g. "Glyphosate 450 g/L" — many products share a near-identical name
  // and are only actually distinguished by active ingredient strength.
  activeIngredientDetails?: string;
}

interface ImportRow {
  reportId: number;
  businessName?: string;
  farmName?: string;
  fieldName?: string;
  seasonName?: string;
  productName?: string;
  productCategoryName?: string;
  geneStack?: string;
  rate?: number;
  unitName?: string;
}

interface Suggestions {
  alreadyMatched: boolean;
  existingRecordId?: string;
  business?: Option;
  farm?: Option;
  field?: Option;
  timePeriod?: Option;
  product?: Option;
  productCategory?: Option;
  unit?: Option;
  operationType?: Option;
  region?: Option;
  geneStack?: Option;
  suggestedTimePeriodStart?: string;
  suggestedTimePeriodEnd?: string;
  similarUnmatchedCount: number;
}

type Mode = 'existing' | 'new' | 'skip';

interface FieldState {
  mode: Mode;
  existingId: string | null;
  newName: string;
}

const emptyField = (): FieldState => ({ mode: 'existing', existingId: null, newName: '' });

interface Props {
  row: ImportRow;
  open: boolean;
  onClose: () => void;
  onMatched: () => void;
}

// One step per resolvable thing (Business -> Farm -> Field -> Season ->
// Product & Category -> Rate Unit & Operation Type -> Review), following the
// existing entities' real names/relationships rather than a generic form.
const steps = ['Business', 'Farm', 'Field', 'Season', 'Product & Category', 'Rate Unit & Operation', 'Review'];

const ImportMatchWizard: React.FC<Props> = ({ row, open, onClose, onMatched }) => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);

  const [businesses, setBusinesses] = useState<Option[]>([]);
  const [farms, setFarms] = useState<Option[]>([]);
  const [fields, setFields] = useState<Option[]>([]);
  const [timePeriods, setTimePeriods] = useState<Option[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [productCategories, setProductCategories] = useState<Option[]>([]);
  const [units, setUnits] = useState<Option[]>([]);
  const [operationTypes, setOperationTypes] = useState<Option[]>([]);
  const [regions, setRegions] = useState<Option[]>([]);
  const [geneStacks, setGeneStacks] = useState<Option[]>([]);

  const [business, setBusiness] = useState<FieldState>(emptyField());
  const [businessParentId, setBusinessParentId] = useState<string | null>(null);
  const [farm, setFarm] = useState<FieldState>({ ...emptyField(), mode: 'skip' });
  const [field, setFieldState] = useState<FieldState>({ ...emptyField(), mode: 'skip' });
  const [timePeriod, setTimePeriod] = useState<FieldState>({ ...emptyField(), mode: 'skip' });
  const [timePeriodStart, setTimePeriodStart] = useState('');
  const [timePeriodEnd, setTimePeriodEnd] = useState('');
  const [product, setProduct] = useState<FieldState>(emptyField());
  const [productSearch, setProductSearch] = useState('');
  const [category, setCategory] = useState<FieldState>({ ...emptyField(), mode: 'skip' });
  const [geneStack, setGeneStack] = useState<FieldState>({ ...emptyField(), mode: 'skip' });
  const [unit, setUnit] = useState<FieldState>(emptyField());
  const [operationTypeId, setOperationTypeId] = useState<string | null>(null);
  const [regionId, setRegionId] = useState<string | null>(null);
  const [applyToSimilar, setApplyToSimilar] = useState(true);

  const productSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset everything each time the dialog opens for a (possibly different) row.
  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setError(null);
    setSuggestions(null);
    setLoading(true);

    (async () => {
      try {
        const [suggestionsRes, businessesRes] = await Promise.all([
          fetch(`/api/import-report-data/${row.reportId}/match-suggestions`, { headers: authHeaders() }),
          fetch('/api/businesses/list', { headers: authHeaders() }),
        ]);
        if (!suggestionsRes.ok) throw new Error('Failed to load match suggestions');
        const s: Suggestions = await suggestionsRes.json();
        setSuggestions(s);
        if (businessesRes.ok) {
          const list = await businessesRes.json();
          setBusinesses(list.map((b: any) => ({ id: b.id, name: b.name })));
        }

        if (s.alreadyMatched) {
          setLoading(false);
          return;
        }

        setBusiness(
          s.business
            ? { mode: 'existing', existingId: s.business.id, newName: row.businessName || '' }
            : { mode: 'new', existingId: null, newName: row.businessName || '' }
        );
        setFarm(
          row.farmName
            ? s.farm
              ? { mode: 'existing', existingId: s.farm.id, newName: row.farmName }
              : { mode: 'new', existingId: null, newName: row.farmName }
            : { mode: 'skip', existingId: null, newName: '' }
        );
        setFieldState(
          row.fieldName
            ? s.field
              ? { mode: 'existing', existingId: s.field.id, newName: row.fieldName }
              : { mode: 'new', existingId: null, newName: row.fieldName }
            : { mode: 'skip', existingId: null, newName: '' }
        );
        setTimePeriod(
          row.seasonName
            ? s.timePeriod
              ? { mode: 'existing', existingId: s.timePeriod.id, newName: row.seasonName }
              : { mode: 'new', existingId: null, newName: row.seasonName }
            : { mode: 'skip', existingId: null, newName: '' }
        );
        setTimePeriodStart(s.suggestedTimePeriodStart || '');
        setTimePeriodEnd(s.suggestedTimePeriodEnd || '');
        setProduct(
          s.product
            ? { mode: 'existing', existingId: s.product.id, newName: row.productName || '' }
            : { mode: 'new', existingId: null, newName: row.productName || '' }
        );
        setCategory(
          row.productCategoryName
            ? s.productCategory
              ? { mode: 'existing', existingId: s.productCategory.id, newName: row.productCategoryName }
              : { mode: 'new', existingId: null, newName: row.productCategoryName }
            : { mode: 'skip', existingId: null, newName: '' }
        );
        setUnit(
          s.unit
            ? { mode: 'existing', existingId: s.unit.id, newName: row.unitName || '' }
            : { mode: 'new', existingId: null, newName: row.unitName || '' }
        );
        setGeneStack(
          row.geneStack
            ? s.geneStack
              ? { mode: 'existing', existingId: s.geneStack.id, newName: row.geneStack }
              : { mode: 'new', existingId: null, newName: row.geneStack }
            : { mode: 'skip', existingId: null, newName: '' }
        );
        setOperationTypeId(s.operationType?.id ?? null);
        setRegionId(s.region?.id ?? null);
        setApplyToSimilar(s.similarUnmatchedCount > 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row.reportId]);

  // Farm options load once a business is resolved (existing or freshly picked).
  const resolvedBusinessId = business.mode === 'existing' ? business.existingId : null;
  useEffect(() => {
    if (!resolvedBusinessId) {
      setFarms([]);
      return;
    }
    (async () => {
      const [layersRes, featuresRes] = await Promise.all([
        fetch(`/api/businesses/${resolvedBusinessId}/spatial-layers`, { headers: authHeaders() }),
        fetch(`/api/businesses/${resolvedBusinessId}/spatial-features`, { headers: authHeaders() }),
      ]);
      if (!layersRes.ok || !featuresRes.ok) return;
      const layers = await layersRes.json();
      const features = await featuresRes.json();
      const farmLayerIds = new Set(layers.filter((l: any) => l.category === 'Farm').map((l: any) => l.id));
      setFarms(features.filter((f: any) => farmLayerIds.has(f.spatialLayerId)).map((f: any) => ({ id: f.id, name: f.name })));
    })();
  }, [resolvedBusinessId, authHeaders]);

  // Field options load once a farm is resolved.
  const resolvedFarmId = farm.mode === 'existing' ? farm.existingId : null;
  useEffect(() => {
    if (!resolvedBusinessId || !resolvedFarmId) {
      setFields([]);
      return;
    }
    (async () => {
      const [layersRes, featuresRes] = await Promise.all([
        fetch(`/api/businesses/${resolvedBusinessId}/spatial-layers`, { headers: authHeaders() }),
        fetch(`/api/businesses/${resolvedBusinessId}/spatial-features`, { headers: authHeaders() }),
      ]);
      if (!layersRes.ok || !featuresRes.ok) return;
      const layers = await layersRes.json();
      const features = await featuresRes.json();
      const fieldLayerIds = new Set(
        layers.filter((l: any) => l.category === 'Field' && l.parentSpatialFeatureId === resolvedFarmId).map((l: any) => l.id)
      );
      setFields(features.filter((f: any) => fieldLayerIds.has(f.spatialLayerId)).map((f: any) => ({ id: f.id, name: f.name })));
    })();
  }, [resolvedBusinessId, resolvedFarmId, authHeaders]);

  // Small reference lists — fetched once when the dialog opens.
  useEffect(() => {
    if (!open) return;
    (async () => {
      const [tpRes, catRes, unitRes, opRes, regionRes, geneStackRes] = await Promise.all([
        fetch('/api/time-periods', { headers: authHeaders() }),
        fetch('/api/product-categories', { headers: authHeaders() }),
        fetch('/api/units', { headers: authHeaders() }),
        fetch('/api/operation-types', { headers: authHeaders() }),
        fetch('/api/regions', { headers: authHeaders() }),
        fetch('/api/gene-stacks', { headers: authHeaders() }),
      ]);
      if (tpRes.ok) setTimePeriods((await tpRes.json()).map((t: any) => ({ id: t.id, name: t.name })));
      if (catRes.ok) setProductCategories((await catRes.json()).map((c: any) => ({ id: c.id, name: c.name })));
      if (unitRes.ok) setUnits((await unitRes.json()).map((u: any) => ({ id: u.id, name: u.name })));
      if (opRes.ok) setOperationTypes((await opRes.json()).map((t: any) => ({ id: t.id, name: t.name })));
      if (regionRes.ok) setRegions((await regionRes.json()).map((r: any) => ({ id: r.id, name: r.name })));
      if (geneStackRes.ok) setGeneStacks((await geneStackRes.json()).map((g: any) => ({ id: g.id, name: g.name })));
    })();
  }, [open, authHeaders]);

  // Debounced product search — same 350ms pattern as ProductManagement.
  useEffect(() => {
    if (productSearchDebounce.current) clearTimeout(productSearchDebounce.current);
    productSearchDebounce.current = setTimeout(async () => {
      const params = new URLSearchParams({ pageSize: '25' });
      if (productSearch) params.set('search', productSearch);
      const res = await fetch(`/api/products?${params.toString()}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProducts(
          data.items.map((p: any) => ({
            id: p.id,
            name: p.name,
            activeIngredientDetails: (p.activeIngredientDetails || []).join(', ') || undefined,
          }))
        );
      }
    }, 350);
    return () => {
      if (productSearchDebounce.current) clearTimeout(productSearchDebounce.current);
    };
  }, [productSearch, authHeaders]);

  // Shown only for seed-variety rows — going purely off the row's raw
  // ProductCategoryName/GeneStack text (the same signal ImportMatchingService
  // uses to suggest an Operation Type of "Planting"), not the in-progress
  // Category selection, since Gene Stack needs to render before Category is
  // necessarily resolved.
  const isSeedRow = !!(row.productCategoryName?.toLowerCase().includes('seed') || row.geneStack);

  const needsFarm = farm.mode !== 'skip';
  const needsField = field.mode !== 'skip';
  const usesSpatialFallback = !needsFarm; // Field always requires a Farm

  const canAdvance = useMemo(() => {
    switch (activeStep) {
      case 0: // Business
        return business.mode === 'existing' ? !!business.existingId : !!business.newName.trim() && !!businessParentId;
      case 1: // Farm
        return farm.mode === 'skip' || (farm.mode === 'existing' ? !!farm.existingId : !!farm.newName.trim());
      case 2: // Field
        return field.mode === 'skip' || (field.mode === 'existing' ? !!field.existingId : !!field.newName.trim());
      case 3: // Season
        return (
          timePeriod.mode === 'skip' ||
          (timePeriod.mode === 'existing'
            ? !!timePeriod.existingId
            : !!timePeriod.newName.trim() && !!timePeriodStart && !!timePeriodEnd)
        );
      case 4: // Product & Category
        return product.mode === 'existing' ? !!product.existingId : !!product.newName.trim();
      case 5: // Unit & Operation
        return (unit.mode === 'existing' ? !!unit.existingId : !!unit.newName.trim()) && !!operationTypeId;
      default:
        return true;
    }
  }, [activeStep, business, businessParentId, farm, field, timePeriod, timePeriodStart, timePeriodEnd, product, unit, operationTypeId]);

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        business:
          business.mode === 'existing'
            ? { existingId: business.existingId }
            : { newName: business.newName.trim(), newParentBusinessId: businessParentId },
        farm:
          farm.mode === 'skip'
            ? null
            : farm.mode === 'existing'
              ? { existingId: farm.existingId }
              : { newName: farm.newName.trim() },
        field:
          field.mode === 'skip'
            ? null
            : field.mode === 'existing'
              ? { existingId: field.existingId }
              : { newName: field.newName.trim() },
        timePeriod:
          timePeriod.mode === 'skip'
            ? null
            : timePeriod.mode === 'existing'
              ? { existingId: timePeriod.existingId }
              : { newName: timePeriod.newName.trim(), newStartDate: timePeriodStart, newEndDate: timePeriodEnd },
        product: {
          existingId: product.mode === 'existing' ? product.existingId : null,
          newName: product.mode === 'new' ? product.newName.trim() : null,
          category:
            category.mode === 'skip'
              ? null
              : category.mode === 'existing'
                ? { existingId: category.existingId }
                : { newName: category.newName.trim() },
          geneStack:
            !isSeedRow || geneStack.mode === 'skip'
              ? null
              : geneStack.mode === 'existing'
                ? { existingId: geneStack.existingId }
                : { newName: geneStack.newName.trim() },
        },
        unit: unit.mode === 'existing' ? { existingId: unit.existingId } : { newName: unit.newName.trim() },
        operationTypeId,
        regionId: usesSpatialFallback ? regionId : null,
        applyToSimilar,
      };

      const response = await fetch(`/api/import-report-data/${row.reportId}/match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save match');
      }
      onMatched();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const renderModeField = (
    optionLabel: string,
    fieldState: FieldState,
    setFieldStateFn: (s: FieldState) => void,
    options: Option[],
    suggestion?: Option,
    allowSkip = false,
    newNameLabel = 'Name'
  ) => (
    <Box>
      <ToggleButtonGroup
        value={fieldState.mode}
        exclusive
        size="small"
        onChange={(_, value) => value && setFieldStateFn({ ...fieldState, mode: value })}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="existing">Use Existing</ToggleButton>
        <ToggleButton value="new">Create New</ToggleButton>
        {allowSkip && <ToggleButton value="skip">None</ToggleButton>}
      </ToggleButtonGroup>

      {suggestion && fieldState.mode === 'existing' && fieldState.existingId === suggestion.id && (
        <Chip label={`Suggested match: ${suggestion.name}`} size="small" color="primary" variant="outlined" sx={{ mb: 2, display: 'block', width: 'fit-content' }} />
      )}

      {fieldState.mode === 'existing' && (
        <Autocomplete
          options={options}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={options.find((o) => o.id === fieldState.existingId) || null}
          onChange={(_, value) => setFieldStateFn({ ...fieldState, existingId: value?.id || null })}
          renderInput={(params) => <TextField {...params} label={`Existing ${optionLabel}`} />}
        />
      )}
      {fieldState.mode === 'new' && (
        <TextField
          fullWidth
          label={newNameLabel}
          value={fieldState.newName}
          onChange={(e) => setFieldStateFn({ ...fieldState, newName: e.target.value })}
        />
      )}
    </Box>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            {renderModeField('Business', business, setBusiness, businesses, suggestions?.business)}
            {business.mode === 'new' && (
              <Autocomplete
                sx={{ mt: 2 }}
                options={businesses}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={businesses.find((o) => o.id === businessParentId) || null}
                onChange={(_, value) => setBusinessParentId(value?.id || null)}
                renderInput={(params) => <TextField {...params} label="Parent Business (required)" helperText="New businesses must be grouped under an existing one" />}
              />
            )}
          </Box>
        );
      case 1:
        return renderModeField('Farm', farm, setFarm, farms, suggestions?.farm, true, 'Farm Name');
      case 2:
        return needsFarm ? (
          renderModeField('Field', field, setFieldState, fields, suggestions?.field, true, 'Field Name')
        ) : (
          <Alert severity="info">No Farm resolved — Field is skipped.</Alert>
        );
      case 3:
        return (
          <Box>
            {renderModeField('Season', timePeriod, setTimePeriod, timePeriods, suggestions?.timePeriod, true, 'Season Name')}
            {timePeriod.mode === 'new' && (
              <Box display="flex" gap={2} mt={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={timePeriodStart}
                  onChange={(e) => setTimePeriodStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={timePeriodEnd}
                  onChange={(e) => setTimePeriodEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
            )}
          </Box>
        );
      case 4:
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Product</Typography>
            <ToggleButtonGroup
              value={product.mode}
              exclusive
              size="small"
              onChange={(_, value) => value && setProduct({ ...product, mode: value })}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="existing">Use Existing</ToggleButton>
              <ToggleButton value="new">Create New</ToggleButton>
            </ToggleButtonGroup>
            {suggestions?.product && product.mode === 'existing' && product.existingId === suggestions.product.id && (
              <Chip label={`Suggested match: ${suggestions.product.name}`} size="small" color="primary" variant="outlined" sx={{ mb: 2, display: 'block', width: 'fit-content' }} />
            )}
            {product.mode === 'existing' ? (
              <>
                <Autocomplete
                  options={products}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={products.find((o) => o.id === product.existingId) || null}
                  onInputChange={(_, value) => setProductSearch(value)}
                  onChange={(_, value) => setProduct({ ...product, existingId: value?.id || null })}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        {option.activeIngredientDetails && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {option.activeIngredientDetails}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => <TextField {...params} label="Search products" />}
                />
                {(() => {
                  const selected = products.find((o) => o.id === product.existingId);
                  return selected?.activeIngredientDetails ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ml: 1.5 }}>
                      {selected.activeIngredientDetails}
                    </Typography>
                  ) : null;
                })()}
              </>
            ) : (
              <TextField fullWidth label="Product Name" value={product.newName} onChange={(e) => setProduct({ ...product, newName: e.target.value })} />
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Category</Typography>
            {renderModeField('Category', category, setCategory, productCategories, suggestions?.productCategory, true, 'Category Name')}

            {isSeedRow && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Gene Stack</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Only applied if the resolved product doesn't already have one — an existing seed variety's gene stack won't be overwritten.
                </Typography>
                {renderModeField('Gene Stack', geneStack, setGeneStack, geneStacks, suggestions?.geneStack, true, 'Gene Stack Name')}
              </>
            )}
          </Box>
        );
      case 5:
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Rate Unit</Typography>
            {renderModeField('Unit', unit, setUnit, units, suggestions?.unit, false, 'Unit Name (e.g. Kg/Ha)')}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Operation Type</Typography>
            <Autocomplete
              options={operationTypes}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={operationTypes.find((o) => o.id === operationTypeId) || null}
              onChange={(_, value) => setOperationTypeId(value?.id || null)}
              renderInput={(params) => <TextField {...params} label="Operation Type" />}
            />
            {suggestions?.operationType && operationTypeId === suggestions.operationType.id && (
              <Chip label={`Suggested: ${suggestions.operationType.name}`} size="small" color="primary" variant="outlined" sx={{ mt: 1 }} />
            )}

            {usesSpatialFallback && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Region (spatial fallback — no Farm resolved)</Typography>
                <Autocomplete
                  options={regions}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={regions.find((o) => o.id === regionId) || null}
                  onChange={(_, value) => setRegionId(value?.id || null)}
                  renderInput={(params) => <TextField {...params} label="Region (optional)" />}
                />
              </>
            )}
          </Box>
        );
      case 6: {
        const businessLabel = business.mode === 'existing' ? businesses.find((b) => b.id === business.existingId)?.name : `${business.newName} (new)`;
        const farmLabel = farm.mode === 'skip' ? 'None' : farm.mode === 'existing' ? farms.find((f) => f.id === farm.existingId)?.name : `${farm.newName} (new)`;
        const fieldLabel = field.mode === 'skip' ? 'None' : field.mode === 'existing' ? fields.find((f) => f.id === field.existingId)?.name : `${field.newName} (new)`;
        const seasonLabel = timePeriod.mode === 'skip' ? 'None' : timePeriod.mode === 'existing' ? timePeriods.find((t) => t.id === timePeriod.existingId)?.name : `${timePeriod.newName} (new)`;
        const selectedProduct = product.mode === 'existing' ? products.find((p) => p.id === product.existingId) : undefined;
        const productLabel = product.mode === 'existing' ? selectedProduct?.name || suggestions?.product?.name : `${product.newName} (new)`;
        const categoryLabel = category.mode === 'skip' ? 'None' : category.mode === 'existing' ? productCategories.find((c) => c.id === category.existingId)?.name : `${category.newName} (new)`;
        const geneStackLabel = !isSeedRow || geneStack.mode === 'skip' ? 'None' : geneStack.mode === 'existing' ? geneStacks.find((g) => g.id === geneStack.existingId)?.name : `${geneStack.newName} (new)`;
        const unitLabel = unit.mode === 'existing' ? units.find((u2) => u2.id === unit.existingId)?.name : `${unit.newName} (new)`;
        const operationLabel = operationTypes.find((o) => o.id === operationTypeId)?.name;
        const regionLabel = regionId ? regions.find((r) => r.id === regionId)?.name : 'None';

        return (
          <Box>
            <Typography variant="body2">Business: <strong>{businessLabel}</strong></Typography>
            <Typography variant="body2">Farm: <strong>{farmLabel}</strong></Typography>
            <Typography variant="body2">Field: <strong>{fieldLabel}</strong></Typography>
            <Typography variant="body2">Season: <strong>{seasonLabel}</strong></Typography>
            <Typography variant="body2">
              Product: <strong>{productLabel}</strong>
              {selectedProduct?.activeIngredientDetails && ` (${selectedProduct.activeIngredientDetails})`}
            </Typography>
            <Typography variant="body2">Category: <strong>{categoryLabel}</strong></Typography>
            {isSeedRow && <Typography variant="body2">Gene Stack: <strong>{geneStackLabel}</strong></Typography>}
            <Typography variant="body2">Rate: <strong>{row.rate ?? '-'} {unitLabel}</strong></Typography>
            <Typography variant="body2">Operation Type: <strong>{operationLabel}</strong></Typography>
            {usesSpatialFallback && <Typography variant="body2">Region (fallback): <strong>{regionLabel}</strong></Typography>}

            {suggestions && suggestions.similarUnmatchedCount > 0 && (
              <FormControlLabel
                sx={{ mt: 2 }}
                control={<Checkbox checked={applyToSimilar} onChange={(e) => setApplyToSimilar(e.target.checked)} />}
                label={`Also apply this same match to ${suggestions.similarUnmatchedCount} other unmatched row(s) with the exact same Business/Farm/Field/Season/Product`}
              />
            )}
          </Box>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Match Report #{row.reportId}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : suggestions?.alreadyMatched ? (
          <Alert severity="info">
            This row is already matched to Record {suggestions.existingRecordId}.
          </Alert>
        ) : (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {renderStepContent()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!loading && !suggestions?.alreadyMatched && (
          <>
            {activeStep > 0 && <Button onClick={() => setActiveStep((s) => s - 1)}>Back</Button>}
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" disabled={!canAdvance} onClick={() => setActiveStep((s) => s + 1)}>
                Next
              </Button>
            ) : (
              <Button variant="contained" disabled={saving} onClick={handleConfirm}>
                {saving ? 'Saving...' : 'Confirm Match'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportMatchWizard;
